import { useEffect, useMemo } from "react";

import ArAgingWidget from "./ArAgingWidget";
import RevenueTabWidget from "./RevenueTabWidget";
import Chart, { type SupportedChartType } from "./Chart";
import ChartWidget from "./ChartWidget";
import HTMLStrip from "./HTMLStrip";
import KPITile from "./KPITile";
import { useFlagMap } from "../lib/flagContext";
import { describeFlag, lookupFlag, soleValue } from "../lib/flagMap.mjs";
import { widgetDomain } from "../lib/widgetDomain.mjs";
import KpiComparisonTile from "./KpiComparisonTile";
import MeasurePivotTable from "./MeasurePivotTable";
import PivotTable from "./PivotTable";
import PresentedTable from "./PresentedTable";
import UpgradeUnlock from "./UpgradeUnlock";
import { WidgetNoteFrame } from "./WidgetNote";
import EmptyState from "./states/EmptyState";
import ErrorState from "./states/ErrorState";
import Skeleton from "./states/Skeleton";

import {
  effectiveChartType,
  isKpiComparisonField,
  type ChartType,
  type ManifestWidget,
} from "../lib/manifest";
import { obfuscatePII, useDemoMode } from "../lib/demoMode";
import { asKpiFormat, formatValue } from "../lib/format";
import { fillDerivedRatioColumns } from "../lib/derivedRatioRows.mjs";
import { resolveFeedGap } from "../lib/missingFeed.mjs";
import { resolveFeedLag } from "../lib/feedLag.mjs";
import { useFeedLag } from "../lib/feedLagData";
import { widgetRangeLabel } from "../lib/globalFilters";
import { grainMismatchCopy } from "../lib/periodWindow.mjs";
import { resolvedWindow, resolvedWindowReason, tileGrainLabel } from "../lib/tileWindow.mjs";
import { cappedListNote } from "../lib/pivotShape.mjs";
import { deltaPct, trendWithheld } from "../lib/priorWindow.mjs";
import {
  DERIVED_RATIOS,
  PERIOD_GRAIN_TABLES,
  TIMELINE_DAY_RANGES,
  canonicalColumnName,
  resolveSourceName,
  useWidgetQuery,
} from "../lib/queries";
import {
  DEFINITION_GATE_COPY,
  GATE_COPY,
  defaultBoundsForColumn,
  resolveTileGate,
  isColumnMissingError,
} from "../lib/sanityGate";
import { presentTable } from "../lib/tablePresentation";
import { hasTileInfo, noteSections } from "../lib/widgetNote.mjs";

/**
 * Type-aware dispatcher (MER-114 Phase 4). Reads one manifest widget,
 * runs its Supabase query via useWidgetQuery, and mounts the
 * component that matches its chart_type.
 *
 * No backend dependency from this component; the query layer
 * encapsulates Supabase access. WidgetRenderer is pure presentation +
 * routing of (manifest, data) -> (chart_type) -> Component.
 */

export interface WidgetRendererProps {
  widget: ManifestWidget;
  /** Optional override: skip the live query and use static data
   * (used by the widget gallery for visual review). */
  mockData?: import("../lib/queries").WidgetData;
  /** Click handler. Phase 5 dashboards pass the drill-down opener
   * here; the gallery omits it for non-interactive review. */
  onSelect?: (widget: ManifestWidget) => void;
  /** MER-625: footer copy for a tile whose click navigates to another tab's
   * detail instead of opening the row-level modal ("Break down in
   * Operations"). Resolved by the page, which is the only caller holding the
   * manifest. Unset leaves the default "Open source view". Today only the
   * single-number footer renders it; a chart or table drill would navigate
   * correctly but carry no footer. */
  drillLabel?: string;
}

const SUPPORTED_CHART_TYPES: Record<ChartType, SupportedChartType | null> = {
  bar: "bar",
  stacked_bar: "stackedBar",
  line: "line",
  line_area: "lineArea",
  pie: "pie",
  single_number: null,
  kpi_comparison: null,
  pivot: null,
  tabular: null,
  html_strip: null,
  ar_aging: null,
  ro_efficiency: null,
  // Rendered by ContainerInventoryWidget (CustomerDashboard dispatches it).
  container_inventory: null,
  // Revenue tab (wave 2): drawn by RevenueTabWidget from its own grouped reads.
  revenue_tab: null,
};

function chartDataFromRows(
  rows: Record<string, unknown>[],
  widget: ManifestWidget,
): { labels: string[]; values: number[] } {
  if (!widget.field || isKpiComparisonField(widget.field)) {
    return { labels: [], values: [] };
  }
  // Heuristic: first non-numeric column is the label axis; the
  // widget.field.column is the measure. Phase 5 can extend this with
  // a widget.dimension declaration when more control is needed.
  const measureKey = widget.field.column;
  let labelKey: string | null = null;
  for (const r of rows) {
    for (const k of Object.keys(r)) {
      if (k === measureKey || k === "tenant_id") continue;
      const v = r[k];
      if (typeof v === "string" || v instanceof Date) {
        labelKey = k;
        break;
      }
    }
    if (labelKey) break;
  }
  const labels: string[] = [];
  const values: number[] = [];
  for (const r of rows) {
    labels.push(String((labelKey && r[labelKey]) ?? ""));
    const n = Number(r[measureKey] ?? 0);
    values.push(Number.isNaN(n) ? 0 : n);
  }
  return { labels, values };
}

/**
 * MER-636: the widget's EFFECTIVE period grain. Widgets reach the renderer
 * already through applyGlobalFilters, so filter.period_type carries the
 * declared grain, the explicit Period selection, or the MER-362 auto-applied
 * global value, whichever won.
 */
function periodGrainOf(widget: ManifestWidget): string | undefined {
  const raw = widget.filter?.period_type;
  if (typeof raw === "string") return raw;
  if (raw && typeof raw === "object" && "value" in raw && typeof raw.value === "string") {
    return raw.value;
  }
  return undefined;
}

/**
 * MER-380: chart types that already print their own name and description.
 *
 * A single-number tile renders its title and `what_it_shows` inside KPITile,
 * and a kpi_comparison tile takes both as props. An html_strip is the
 * manifest's own markup and owns its layout. Everything else (bar, line, pie,
 * stacked bar, area, pivot, tabular) had no heading of any kind.
 */
const SELF_CAPTIONED_TYPES = new Set(["single_number", "kpi_comparison", "html_strip"]);

const BLOCKED_STATUS_CAPTION: Record<string, string> = {
  blocked: "Pending. Source data not yet available.",
  deferred: "Deferred. Not in scope for the current build phase.",
  suppressed: "Suppressed. Hidden until the source connector ships.",
  // MER-694: resolve-manifest stamps this on a widget scoped to service lines
  // (e.g. Roll-Off only) that this customer does not run, instead of widening
  // the scope to the lines they do run. Reaches the renderer only from a
  // show_pending_widgets build; the customer view drops the tile (MER-577).
  not_offered: "Not offered. This tile covers a service line you do not run.",
};

export default function WidgetRenderer(props: WidgetRendererProps) {
  const { widget, mockData, onSelect, drillLabel } = props;
  // useWidgetQuery must run unconditionally (Rules of Hooks). The query
  // hook is no-op-safe when the widget is blocked because executeKpiQuery
  // bails on missing source/field (MER-133); we just don't read the data.
  const live = useWidgetQuery(widget);
  // MER-576: the tenant's benchmark verdicts, already fetched once for the
  // page by FlagMapProvider. Context read, not a query -- must run
  // unconditionally like every other hook here.
  const flagMap = useFlagMap();
  // MER-291: hook must run unconditionally. Used by:
  //   - tabular / chart / pivot branches to gate PII obfuscation
  //   - the caption resolver below (MER-292)
  const demoMode = useDemoMode();
  // MER-292: in demo mode prefer widget.sales_caption (value-prop
  // hauler-literacy framing) over widget.what_it_shows (operator
  // jargon). Falls through to what_it_shows when sales_caption is
  // absent or demo mode is off.
  const caption = demoMode && widget.sales_caption ? widget.sales_caption : widget.what_it_shows;

  // MER-693: how far this widget's source actually goes.
  //
  // Hoisted here for three reasons: the note it produces rides the existing
  // periodNote chip built just below, the request hook must run on EVERY
  // render (Rules of Hooks) including the blocked path where no query runs,
  // and sourceName is wanted by both this block and the feed-gap resolver
  // further down.
  //
  // The probe itself is one read per (table, timeline column) for the whole
  // page, cached by FeedLagProvider the way FlagMapProvider caches the flag
  // map. An unknown answer, in flight, failed, or an empty source, leaves
  // every tile exactly as it behaves today.
  const sourceName = widget.source ? resolveSourceName(widget.source) : undefined;
  const feedLag = useFeedLag();
  const timelineColumn = widget.timeline?.column
    ? canonicalColumnName(widget.timeline.column)
    : undefined;
  // Period-bucketed sources are excluded on purpose. useWidgetQuery already
  // refuses their vs-prior pass outright (the partial-bucket trap) and
  // periodWindow.mjs already ends their trailing windows at the last COMPLETE
  // bucket, so guarding them here would answer an answered question twice.
  const isPeriodGrainSource = sourceName ? PERIOD_GRAIN_TABLES.has(sourceName) : false;
  // A manifest-blocked widget renders the awaiting card and runs no query at
  // all, so a probe on its behalf buys a round trip nothing can consume.
  const manifestBlocked = Boolean(widget.status && widget.status !== "ok");
  const wantsDataThrough =
    Boolean(sourceName) &&
    Boolean(timelineColumn) &&
    !isPeriodGrainSource &&
    !manifestBlocked &&
    widget.timeline?.mode !== "snapshot" &&
    widget.kpi_subtype !== "snapshot";
  const requestDataThrough = feedLag.request;
  useEffect(() => {
    if (!wantsDataThrough) return;
    requestDataThrough(sourceName, timelineColumn);
  }, [wantsDataThrough, sourceName, timelineColumn, requestDataThrough]);
  // MER-294: demo mode rewrites every range to ytd inside applyTimeline, so
  // the window the query actually ran is not the one the manifest declares.
  // The guard reads the same effective range, or it falls silent in the one
  // mode that renders in front of people seeing this data for the first time,
  // and silent precisely on the tiles whose window just got WIDER.
  const effectiveTimeline = useMemo(
    () => (demoMode && widget.timeline ? { ...widget.timeline, range: "ytd" } : widget.timeline),
    [demoMode, widget.timeline],
  );
  // One clock per mount. Allocating a Date in the render body handed every
  // widget on the page its own and let a verdict flip under a session left
  // open across local midnight.
  const today = useMemo(() => new Date(), []);
  const feedLagVerdict = resolveFeedLag({
    timeline: effectiveTimeline,
    periodGrain: isPeriodGrainSource,
    dataThrough: wantsDataThrough ? feedLag.lookup(sourceName, timelineColumn) : undefined,
    today,
  });

  // MER-421 Root 2: render the EFFECTIVE window as part of the title so the label
  // always matches the data window -- the Default "last complete month", or the
  // user's selected range -- instead of a stale static "(Last 30 days)" baked into
  // the name. Only period-mode widgets whose effective range has a window label;
  // trailing-window / trend widgets (widgetRangeLabel -> null) keep their name.
  // MER-692: the label also reads the effective grain, so an explicit Weekly
  // under a chosen month says "Weeks of Jul 2026", never the bare month.
  // MER-614 defects 2 and 3: on a period-bucketed source a trailing range
  // does not answer with the days it names. It answers with whole buckets
  // ending at the last one that closed, and the tile said neither how many
  // nor through when. "Last 30 days" at weekly grain is four weeks, 28 days,
  // shut two days before the reader is looking at it, and a single-number
  // tile under a "Weekly" chip reads as one week when it is the sum of four.
  //
  // The label is derived from periodWindowBounds, the same function
  // applyTimeline SELECTS the buckets with, so the chip and the query cannot
  // drift. It returns null wherever the range already names a calendar period
  // truthfully (this_month, last_month, a MER-692 month token), on event-grain
  // sources whose day cutoff really is a day cutoff, and wherever
  // periodWindowBounds has no opinion and applyTimeline kept its bare cutoff.
  // In all of those the existing range label stands.
  //
  // Three gates below were found on review, and each of them is the same
  // mistake in a different place: naming a window for a query that did not
  // run, or that ran somewhere else.
  //
  //   - A manifest-BLOCKED tile runs no query at all. It renders "Pending.
  //     Source data not yet available", and a dated window beside that is a
  //     falsifiable claim about work nobody did. It keeps the vague preset
  //     name it had before.
  //   - The MER-470 fallback re-anchors an empty window to the most recent
  //     period that has rows and says so on the face. A tile cannot carry
  //     "3 months to Aug 31" and "showing most recent: July" at once, so the
  //     re-anchored window wins and this label stands down.
  //   - The CLOCK is read fresh rather than taken from the mount-time `today`
  //     above. That memo exists so a feed-lag verdict cannot flip under a
  //     session left open across midnight, which is right for a verdict and
  //     wrong for this: applyTimeline calls `new Date()` at execution, so a
  //     page left open across a month boundary and then filtered would query
  //     three months to Sep 30 while the chip still read "to Aug 31". The
  //     label has to share the query's clock, not the mount's.
  const fallbackReanchored = Boolean(
    (live.data as { fallbackPeriodLabel?: string } | undefined)?.fallbackPeriodLabel,
  );
  const resolvedWindowNote = resolvedWindow({
    grain: periodGrainOf(widget),
    // The EFFECTIVE range, demo-mode rewrite included, because that is the
    // window applyTimeline ran. Reading the declared range here would name a
    // window the tile never asked for, on the one surface people see this
    // data on for the first time.
    range: effectiveTimeline?.range,
    days: TIMELINE_DAY_RANGES[effectiveTimeline?.range ?? ""],
    column: timelineColumn,
    isPeriodGrainSource: isPeriodGrainSource && !manifestBlocked && !fallbackReanchored,
    isSnapshot: widget.kpi_subtype === "snapshot" || widget.timeline?.mode === "snapshot",
    today: new Date(),
  });
  const rangeLabel = resolvedWindowNote?.note ?? widgetRangeLabel(widget);
  // MER-580: the period no longer rides the title. Concatenating name +
  // range + scope_note into one 10px uppercase label produced
  // "RO DELIVERIES · LAST 30 DAYS" wrapping to three lines on a narrow card,
  // which is what pushed the value down and squeezed the caption. The name is
  // the title; the period and any scope note render as their own muted line,
  // where they can wrap without disturbing the number.
  const effectiveTitle = widget.name;
  // MER-693: the feed's last day joins the same chip, and so does the fact
  // that the arrow beside it is being withheld. A reader who can see "Last 30
  // days" has to be able to see that the last week of it is not in yet, and an
  // arrow that simply vanishes leaves them comparing today's tile against
  // yesterday's memory with nothing on the face to explain the absence. The
  // chip is where the scope of a tile already lives; a second channel for the
  // same class of fact would just be one more thing to miss.
  // MER-614: the grain leads the chip, in the same order the filter bar reads
  // it (Period, then Range). Zay, 2026-09-02: "when you go to weekly you still
  // see the last 30 days on disposal". The Overview disposal tiles are
  // authored last_30_days; monthly grain rewrites that to last_month and
  // weekly leaves it alone, so the two grains answer with different numbers
  // and the only thing that moved on the tile was a range label nobody had a
  // reason to connect to the Period selector. A tile on a period-bucketed
  // source and a tile counting raw events can also carry the SAME "Last 30
  // days" over genuinely different windows, because the bucketed one ends at
  // the last complete bucket (periodWindow.mjs). Naming the grain is what
  // tells those two apart. A tile with no grain (event-grain sources carry no
  // period_type) says nothing rather than claiming one.
  const grainNote = tileGrainLabel(periodGrainOf(widget));
  // MER-553: the same rule for the OTHER reason an arrow can be absent. A
  // period-grain tile that asked for a vs-prior comparison never gets one:
  // useWidgetQuery refuses the prior pass outright, because the earlier window
  // is not bucket-aligned and would measure a month still filling against a
  // month that finished. That refusal was correct and silent, and silent is
  // what this fixes. The chip says there is no comparison; the panel below
  // says why. Nothing here computes one.
  const noTrend = trendWithheld({
    trend: widget.trend,
    isPeriodGrain: isPeriodGrainSource,
    isSnapshot: widget.kpi_subtype === "snapshot" || widget.timeline?.mode === "snapshot",
  });
  const periodNote =
    [
      grainNote,
      rangeLabel,
      widget.scope_note,
      feedLagVerdict?.note,
      widget.trend === "vs_prior_period" ? feedLagVerdict?.deltaNote : null,
      noTrend?.note,
    ]
      .filter(Boolean)
      .join(" \u00b7 ") || undefined;
  // MER-614 + MER-616: the same facts for every other widget family. periodNote
  // is a KPITile prop and reaches nothing else, so until now a chart, table or
  // pivot showed neither its grain, its window, nor the scope note saying it
  // could not honour the active LOB chip -- the exact silence Chris read as a
  // broken filter on the Operations tab. The feed sentence keeps its own line
  // below (it is a sentence, not a chip), so it is left out here.
  const faceNote = [grainNote, rangeLabel, widget.scope_note].filter(Boolean).join(" \u00b7 ");

  // MER-604: the manifest's technical definition of this metric, stripped of
  // our own build provenance (ticket ids, PR numbers). 66 DWS widgets and 64
  // Best Disposal widgets carry one and none of them had ever rendered.
  // Distinct from `caption` above: that is what_it_shows, the operator's
  // one-liner on the face of the tile. This answers "where does this number
  // come from" and stays behind a control until asked for.
  // MER-558: what this measures, how, what to do when it moves, and what good
  // looks like -- whichever the manifest carries. The first section reads the
  // SAME `what_it_shows` the caption renders, which is the ticket's "one
  // source" rule: the face of the card and the panel behind it cannot drift.
  //
  // `hasTileInfo` keeps MER-604's behaviour exactly. A widget whose only
  // section would be the caption it already shows gets no control, so the "i"
  // still appears only where there is something behind it.
  const allSections = noteSections({
    measures: caption,
    // MER-553: the reason an honest tile carries no arrow rides with the rest
    // of "how this is measured", because that is what it is: a fact about the
    // shape of the rows, not about the feed behind them.
    // MER-614 defects 2 and 3: so does the reason a 30-day range answered
    // with four weeks, and the fact that a single-number tile adds them up.
    // The chip has room for "4 weeks to Sep 20" and not for why, which is
    // the same split MER-553 made this morning rather than a second channel.
    note:
      [
        widget.widget_note,
        resolvedWindowReason(resolvedWindowNote, {
          range: effectiveTimeline?.range,
          days: TIMELINE_DAY_RANGES[effectiveTimeline?.range ?? ""],
          // Found on review. Keyed on kpi_subtype this told 59 of the 99
          // period-grain single-number tiles on DWS that an average, a
          // maximum or a distinct count was "the total across all 4 of
          // them", eleven of them percentages. Only the AGGREGATION can
          // earn that sentence, and tileWindow.mjs owns which ones do.
          aggregation:
            widget.field && !isKpiComparisonField(widget.field)
              ? widget.field.aggregation
              : undefined,
        }),
        noTrend?.reason,
      ]
        .filter(Boolean)
        .join(" ") || undefined,
    action: widget.action,
    good: widget.good,
  });
  const note = hasTileInfo(allSections) ? allSections : [];
  const noteDomain = widgetDomain(widget);
  // MER-693: charts, tables and pivots carry the feed date as their own muted
  // line, in the same slot the MER-470 fallback label uses. periodNote is a
  // KPITile prop and reaches nothing else, so without this a trend chart whose
  // final weeks slope to zero because the export is late says nothing at all
  // -- the exact symptom this ticket exists to remove, on two of the three
  // widget families its WHY names. It hangs off withNote so every non-KPI
  // branch is covered at one site instead of six.
  const feedNoteLine = feedLagVerdict ? (
    <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 6 }}>
      {feedLagVerdict.note}.
    </div>
  ) : null;
  // MER-614 + MER-616: the grain, the window and the scope note on the face of
  // a chart, table or pivot. Muted line rather than the KPITile chip because
  // these widgets have no domain wash behind them to sit on.
  const faceNoteLine = faceNote ? (
    <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 6 }}>{faceNote}</div>
  ) : null;

  // MER-380: a chart says what it is, on the face of the chart.
  //
  // The ticket opens "today charts have a title but no description". Half of
  // that was optimistic: a chart carried NEITHER. Its name reached only the
  // canvas aria-label, and its description reached nothing at all, because
  // `what_it_shows` is a KPITile prop and hasTileInfo withholds the info
  // control from a widget whose only section is the caption it is supposed to
  // already be showing. Every chart in every tier template declares
  // `what_it_shows` (the pipeline's manifest_lint makes a missing one an
  // error), so the copy was written, reviewed and shipped, and then rendered
  // nowhere. 16 of DWS's 24 charts showed the reader no words at all.
  //
  // ONE SOURCE, NOT A THIRD PLACE. No new manifest field. This reads the same
  // `caption` the KPI tiles read, which is `what_it_shows` (or `sales_caption`
  // under demo mode), so a chart's description cannot drift from its tile's:
  // there is one string. The ontology's `tile_note` continues to reach this
  // copy the way it always has, by composing into the widget's own note at
  // resolve time (resolve-manifest.mjs), which is why extending the ontology
  // to chart bindings was the other half of that change rather than a new
  // channel here.
  //
  // WHERE IT SITS. Above the chart, where a title would be, at the top of the
  // stack MER-614's face note already occupies. A chart has no title element
  // to sit under, so this supplies one, and the grain, window and scope keep
  // their own line directly beneath it.
  const selfCaptioned = SELF_CAPTIONED_TYPES.has(effectiveChartType(widget) ?? "");
  const chartHeading = selfCaptioned ? null : (
    <div style={{ marginBottom: caption ? 6 : 2 }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-1)" }}>{widget.name}</div>
      {caption ? (
        <div style={{ fontSize: 12, color: "var(--text-2)", marginTop: 3, lineHeight: 1.45 }}>
          {caption}
        </div>
      ) : null}
    </div>
  );

  const withNote = (node: React.ReactNode) => {
    const body =
      chartHeading || faceNoteLine || feedNoteLine ? (
        <>
          {chartHeading}
          {faceNoteLine}
          {feedNoteLine}
          {node}
        </>
      ) : (
        <>{node}</>
      );
    return note.length > 0 ? (
      <WidgetNoteFrame sections={note} label={widget.name} domain={noteDomain}>
        {body}
      </WidgetNoteFrame>
    ) : (
      <>{body}</>
    );
  };

  // The column this widget binds. Hoisted above the status short-circuit so the
  // feed resolver can read it on the blocked path too, where no query runs.
  const fieldColumn = widget.field && "column" in widget.field ? widget.field.column : undefined;
  // AJ 2026-09-02: every column this widget touches, for the chart / table
  // shapes that carry their measures in a series or a column allow-list instead
  // of `field`. Vehicle Safety Rankings binds nine columns and no field.
  const boundColumns = [
    fieldColumn,
    widget.value?.column,
    ...(widget.y_axis_series ?? []).map((serie) => serie.column),
    ...(Array.isArray(widget.columns) ? widget.columns : widget.columns ? [widget.columns] : []),
  ].filter((c): c is string => Boolean(c));

  // Short-circuit on manifest-declared status. Prevents rendering Supabase
  // errors for widgets whose source column / table does not exist yet
  // (e.g. overage_capture_rate, MER-135).
  if (widget.status && widget.status !== "ok") {
    // AJ 2026-09-02: when we can name the feed that would fill this widget, say
    // it. "Pending. Source data not yet available." tells an owner nothing they
    // can act on; "awaiting the telematics engine-hours feed" tells them what to
    // switch on. Simon's demo review asked for exactly this on the fleet tiles.
    // resolveFeedGap only answers for blocked / suppressed; a deferred widget is
    // out of scope, not feed-starved, so it keeps the generic copy.
    // MER-717: a board tile blocked on a client feed no column can carry yet
    // names that feed in the manifest (`awaiting_feed`); a registered column
    // still wins inside resolveFeedGap.
    const statusFeedGap = resolveFeedGap({
      columns: boundColumns,
      source: sourceName,
      status: widget.status,
      awaitingFeed: widget.awaiting_feed,
    });
    const caption =
      statusFeedGap?.copy ?? BLOCKED_STATUS_CAPTION[widget.status] ?? `Status: ${widget.status}`;
    // MER-604: a blocked widget is exactly where a reader wants the note --
    // the caption says "pending", the note says what it is pending on.
    return withNote(<EmptyState title={widget.name} caption={caption} />);
  }

  // AJ 2026-09-02: the feed behind a tile with no number, once the widget is
  // live. Two strengths, because they are two different claims:
  //   noSourceFeedGap - the query could not resolve at all for this tenant
  //     (MER-446 phantom column, MER-589 no rows to pin). A feed gap for any
  //     registered column.
  //   emptyFeedGap - the query resolved and returned nothing. Only named for a
  //     column the pipeline can never fill; for anything else an empty result
  //     is a sparse range, and we must not send an owner to connect a feed
  //     they already have.
  const noSourceFeedGap = resolveFeedGap({
    columns: boundColumns,
    source: sourceName,
    status: widget.status,
    noSourceData: true,
  });
  const emptyFeedGap = resolveFeedGap({
    columns: boundColumns,
    source: sourceName,
    status: widget.status,
    isEmpty: true,
  });
  // Zero rows on a chart / table / pivot: name the feed where we know it,
  // otherwise the plain empty tile.
  const emptyRows = () =>
    emptyFeedGap ? (
      <EmptyState title={effectiveTitle} caption={emptyFeedGap.copy} />
    ) : (
      <EmptyState />
    );

  const data = mockData ?? live.data;
  const isLoading = mockData ? false : live.isLoading;
  const error = mockData ? null : live.error;

  const chartType = effectiveChartType(widget);

  if (chartType === "html_strip") {
    return <HTMLStrip html={widget.what_it_shows ?? widget.why ?? ""} />;
  }

  // AR Aging tab (wave 1): the panel fetches and computes its own snapshot
  // (arAgingData.ts + arAging.mjs); useWidgetQuery skips it.
  if (chartType === "ar_aging") {
    return withNote(<ArAgingWidget widget={widget} />);
  }

  // Revenue tab (wave 2): the view reads its own grouped invoice rows
  // (revenueTabData.ts) and computes every figure in revenueTab.mjs;
  // useWidgetQuery skips it. A blocked view never reaches here: it took the
  // status short-circuit above and says what it is awaiting.
  if (chartType === "revenue_tab") {
    return withNote(<RevenueTabWidget widget={widget} />);
  }

  const wrapClickable = onSelect
    ? (node: React.ReactNode) => (
        <div
          role="button"
          tabIndex={0}
          onClick={() => onSelect(widget)}
          onKeyDown={(ev) => {
            if (ev.key === "Enter" || ev.key === " ") {
              ev.preventDefault();
              onSelect(widget);
            }
          }}
          style={{ cursor: "pointer" }}
        >
          {node}
        </div>
      )
    : (node: React.ReactNode) => <>{node}</>;

  if (chartType === undefined) {
    return <ErrorState message={`Unsupported chart_type for widget ${widget.id}`} />;
  }

  if (isLoading) return <Skeleton label={widget.name} />;
  if (error) {
    // MER-446: a "column does not exist" (42703) means the manifest references a
    // phantom column the pipeline does not emit yet. Render the honest "Not
    // available - awaiting source data" state, never a raw DB error in front of a
    // client. Genuine query failures still surface as an error.
    if (isColumnMissingError(error.message)) {
      if (chartType === "single_number") {
        // MER-571: a gated tile is NOT a door. It keeps its title and caption but
        // loses the drill affordance, so the value the gate just withheld cannot
        // be read from the drill-down one click later.
        return (
          <KPITile
            domain={widgetDomain(widget)}
            title={effectiveTitle}
            periodNote={periodNote}
            value={null}
            format={asKpiFormat(widget.display_format)}
            caption={caption}
            sections={note}
            isGated
            gateCopy={noSourceFeedGap?.copy}
          />
        );
      }
      return withNote(
        <EmptyState title={effectiveTitle} caption={noSourceFeedGap?.copy ?? GATE_COPY} />,
      );
    }
    return <ErrorState message={error.message} />;
  }
  if (!data) return <EmptyState />;

  if (chartType === "single_number") {
    if (data.kind !== "kpi") {
      return <ErrorState message="KPI tile expected scalar data, got rows" />;
    }
    // MER-317: pass the query-layer isEmpty signal through so KPITile can
    // render the limited-activity copy instead of bare n/a when the
    // source is wired but the selected range has no matching rows. The
    // widget's name + caption keep rendering either way; only the value
    // glyph swaps. queries.ts owns the rule for what counts as "empty
    // range" vs "valid zero" per aggregation.
    // MER-441/446/449: gate impossible figures at egress. Starved denominators
    // ($46,454/ton, -3500% CM, 4,240% collection) render the honest "awaiting
    // source data" Pending state; calc/binding bugs inside the dashboard
    // (fuel 161% of revenue, 17,880% MoM variance, broken churn / billing
    // accuracy) render "Not available, under review". resolveTileGate owns the
    // precedence and copy; explicit display_bounds win over column defaults.
    const kpiFormat = asKpiFormat(widget.display_format);
    const bounds = widget.display_bounds ?? defaultBoundsForColumn(fieldColumn);
    // MER-715: the query layer can withhold a value itself (a ratio it will not
    // combine, a column whose inputs are known to be incomplete, a cost that is
    // zero only because its feed is missing). That verdict outranks the
    // value-range gate, which has no value to judge.
    // MER-819: a DEFINITION gap outranks every value-level gate, and it
    // outranks them whatever the query came back with. The two gates below
    // both judge a number; this one says there is no agreed question for a
    // number to answer, which is upstream of having one. Forcing it also
    // closes the case where a stale row still carries a value computed under
    // a rule the customer never agreed to: the pipeline writes NULL for these
    // (gate_metric_value), and the tile refuses to print one even if a row
    // survives a refresh that has not run yet.
    const definitionGate = widget.awaiting_definition ? { copy: DEFINITION_GATE_COPY } : null;
    const gate =
      definitionGate ??
      (data.gateCopy
        ? { copy: data.gateCopy }
        : resolveTileGate(data.scalar, kpiFormat, { column: fieldColumn, bounds }));
    // AJ 2026-09-02: the column exists and the query resolved, but this tenant
    // has no value for it. Without this the tile reads "Limited activity this
    // period. Try a wider range." and an owner widens a range that will never
    // fill. Rendered through the existing gated slot -- muted copy, no drill
    // affordance -- rather than a parallel state, so it inherits MER-571's rule
    // that a tile withholding its number must not look like a door.
    const feedGate = !gate && data.isEmpty ? emptyFeedGap : null;
    // MER-636: an empty tile whose selected range cannot address a single
    // bucket at its grain is not a sparse range. Week to date on monthly rows
    // is the case: no monthly period is mostly covered by seven days, so
    // applyTimeline honestly selects nothing, and "Try a wider range" both
    // understates the cause and omits half the remedy (the Period selector is
    // the other half). grainMismatchCopy returns null for every other empty
    // tile, which keeps AJ-owned EMPTY_RANGE_COPY on the sparse-range case it
    // was written for.
    const grainGapCopy =
      data.isEmpty && !gate && !feedGate
        ? grainMismatchCopy({
            grain: periodGrainOf(widget),
            // The EFFECTIVE range, as the window label twelve lines up
            // already reads. Found on review: these two disagreed in demo
            // mode, where applyTimeline rewrites every range to ytd, so this
            // one computed a verdict about a window the tile never ran.
            range: effectiveTimeline?.range,
            days: TIMELINE_DAY_RANGES[effectiveTimeline?.range ?? ""],
          })
        : null;
    // MER-545: "+X.X% vs prior" delta from the prior-window second pass.
    // Suppressed on gated or empty tiles (no trustworthy current value) and
    // by deltaPct's own guards (no baseline, absurd swing).
    // MER-693: and withheld when this window runs MATERIALLY past the end of
    // the feed. A part-populated current window against a full prior one is
    // the same partial-vs-full trap priorWindow.mjs refuses for period-
    // bucketed tables; the difference is only that here the partial half is a
    // feed fact rather than a calendar one. "Materially" is the guard's own
    // proportional test: a shortfall that swallows a week-wide window silences
    // its arrow, while the same shortfall against a year-wide one leaves it
    // alone, because withholding there would cost a real signal to remove a
    // distortion smaller than the noise. The VALUE renders in both cases:
    // those events really happened.
    const delta =
      data.prior !== undefined &&
      !gate &&
      !data.isEmpty &&
      !feedLagVerdict?.suppressDelta &&
      typeof data.scalar === "number"
        ? deltaPct(data.scalar, data.prior)
        : null;
    // MER-560 part 2: owner-entered target flagging. The customer sets their
    // own goal (resolver injects widget.target from customer config); the
    // measured value is flagged against THAT, never a universal benchmark.
    // GREEN at/above target, YELLOW within 80% of it, RED below, mirrored
    // for lower_is_better. Suppressed on gated/empty tiles (nothing
    // trustworthy to flag).
    let flagLevel: "GREEN" | "YELLOW" | "RED" | undefined;
    let targetCaption = caption;
    if (widget.target && typeof widget.target.value === "number") {
      const t = widget.target.value;
      targetCaption = [caption, `Target ${formatValue(t, kpiFormat)} (set by customer)`]
        .filter(Boolean)
        .join(" · ");
      if (!gate && !data.isEmpty && typeof data.scalar === "number") {
        const lower = widget.target.direction === "lower_is_better";
        const v = data.scalar;
        if (lower) {
          flagLevel = v <= t ? "GREEN" : v <= t * 1.25 ? "YELLOW" : "RED";
        } else {
          flagLevel = v >= t ? "GREEN" : v >= t * 0.8 ? "YELLOW" : "RED";
        }
      }
    }

    // MER-576: with no owner target set, fall back to the BENCHMARK/FLAG
    // verdict the pipeline already computes every refresh. Precedence is
    // deliberate and matches MER-560: a customer's own goal always beats a
    // derived benchmark, because a benchmark is our opinion and the target is
    // theirs. Suppressed on gated/empty tiles for the same reason the target
    // flag is -- there is no trustworthy value to judge.
    //
    // The colour carries its reason. A tile that turns yellow without saying
    // what it is yellow against is decoration; "18% above your target 45" is
    // the thing an operator can act on.
    //
    // MER-716: only a standard source (the customer's target, a cited industry
    // reference) may colour the tile. A flag graded against the customer's own
    // history gets no chip and reads as a trend with its source named, because
    // a wrong-but-stable number sits inside its own history and would read
    // green.
    //
    // MER-717: `benchmark_flags: false` opts a tile out. The Meridian Board
    // shows no band or colour derived from our own history (the
    // circular-validation rule), and several board columns share a name with a
    // KPI_REGISTRY key that build_benchmarks derives thresholds for.
    if (
      flagLevel === undefined &&
      widget.benchmark_flags !== false &&
      !gate &&
      !data.isEmpty &&
      typeof data.scalar === "number"
    ) {
      const benchFlag = lookupFlag(
        flagMap,
        fieldColumn,
        soleValue(widget.filter?.equipment_type),
        soleValue(widget.filter?.division),
      );
      if (benchFlag) {
        const described = describeFlag(
          benchFlag,
          benchFlag.benchmarkValue === null
            ? null
            : formatValue(benchFlag.benchmarkValue, kpiFormat),
        );
        flagLevel = described.level;
        if (described.caption) {
          targetCaption = [targetCaption, described.caption].filter(Boolean).join(" \u00b7 ");
        }
      }
    }
    // MER-467: a roll-off cost or margin figure built only from the hauls that
    // came back with a scale ticket says so, on the face of the card, next to
    // the number. It is appended last so it reads after the description and
    // after any target: "what this is, what it is measured against, and how
    // much of the work it actually covers".
    //
    // The tile is NOT gated by it. Blanking the figure would take away a number
    // the client is using and tell them nothing about why; the work orders
    // behind the gap are a reviewable list in the audit, not a hidden tile.
    if (data.coverageNote) {
      targetCaption = [targetCaption, data.coverageNote].filter(Boolean).join(" \u00b7 ");
    }

    // MER-571: when the gate fires, drop the drill affordance with the number.
    // Previously the tile kept role="button" and its drill-down ran select("*"),
    // so a tile reading "Not available - awaiting source data" printed the exact
    // value it had just withheld. Matches the approved mockup, whose pending
    // tiles carry no data-go target at all.
    const wrap = gate || feedGate ? (node: React.ReactNode) => <>{node}</> : wrapClickable;
    return wrap(
      <KPITile
        domain={widgetDomain(widget)}
        title={effectiveTitle}
        periodNote={periodNote}
        drillable={!gate && !data.isEmpty}
        drillLabel={drillLabel}
        value={data.scalar}
        format={kpiFormat}
        caption={targetCaption}
        delta={delta}
        flagLevel={flagLevel}
        isEmpty={data.isEmpty && !feedGate}
        emptyCopy={grainGapCopy ?? widget.empty_copy ?? undefined}
        isGated={Boolean(gate) || Boolean(feedGate)}
        gateCopy={gate?.copy ?? feedGate?.copy}
        sections={note}
      />,
    );
  }

  if (chartType === "kpi_comparison") {
    if (!isKpiComparisonField(widget.field)) {
      return (
        <ErrorState
          message={`Widget ${widget.id}: kpi_comparison needs primary/secondary/tertiary`}
        />
      );
    }
    return withNote(
      wrapClickable(
        <KpiComparisonTile
          widget={widget}
          field={widget.field}
          format={asKpiFormat(widget.display_format)}
          caption={caption}
        />,
      ),
    );
  }

  if (data.kind !== "rows") {
    return <ErrorState message={`Widget ${widget.id} expected row data`} />;
  }

  if (chartType === "tabular") {
    // MER-483 finding 3: fill declared derived-ratio columns (e.g.
    // route gross_margin_pct) from their dollar building blocks where
    // the stored value is null, so tables agree with the charts that
    // compute the same ratio on the same page. Stored values win;
    // un-anchorable rows stay "-".
    const declaredColumns = Array.isArray(widget.columns)
      ? widget.columns
      : widget.columns
        ? [widget.columns]
        : [];
    const rows = fillDerivedRatioColumns(
      data.rows,
      widget.source ? resolveSourceName(widget.source) : null,
      declaredColumns,
      DERIVED_RATIOS,
    );
    if (rows.length === 0) return withNote(emptyRows());
    // MER-438: manifest-driven presentation. The widget's `columns` allow-list
    // (or the default hidden-column filter) decides what renders, headers are
    // humanized, and cells are type-formatted -- currency, fraction-aware
    // percent (MER-184), ISO dates, snake_case enums -- with numerics right
    // aligned. obfuscatePII still masks ids in demo mode (MER-291).
    const presented = presentTable(widget, rows, {
      obfuscate: (value, column) => obfuscatePII(value, column, demoMode),
    });
    return withNote(
      <>
        {/* MER-470: the selected window was empty; these rows are the
            latest populated period. The shift is labeled, never silent. */}
        {data.fallbackPeriodLabel && (
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 6 }}>
            Selected period has no data yet. Showing most recent: {data.fallbackPeriodLabel}.
          </div>
        )}
        {/* Wave 1 Disposal tab: a `row_limit` list says what it left out. */}
        {typeof data.totalCount === "number" && data.totalCount > data.rows.length && (
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 6 }}>
            {cappedListNote(data.rows.length, data.totalCount)}
          </div>
        )}
        <PresentedTable
          columns={presented.columns}
          rows={presented.rows}
          hiddenByCap={presented.hiddenByCap}
        />
      </>,
    );
  }

  if (chartType === "pivot") {
    // Manifest-driven measure-table form: widget.rows + widget.measures.
    // Each measure is its own column with a per-row aggregate. Used by
    // L1.2 Revenue by Operation and any similar "horizontal KPI table"
    // pivot in the DWS manifest. Falls through to the legacy PivotTable
    // (2D row x col x measure) when measures aren't declared.
    if (widget.rows && widget.measures && widget.measures.length > 0) {
      return withNote(
        wrapClickable(
          <MeasurePivotTable
            widget={widget}
            rows={widget.rows}
            measures={widget.measures}
            format={asKpiFormat(widget.display_format)}
          />,
        ),
      );
    }
    const rows = data.rows;
    if (rows.length === 0) return withNote(emptyRows());
    const keys = Object.keys(rows[0]);
    if (keys.length < 3) {
      return <ErrorState message="Pivot widget needs >= 3 columns (row, col, measure)" />;
    }
    const pivotMeasure =
      widget.field && !isKpiComparisonField(widget.field) ? widget.field.column : undefined;
    return withNote(
      <>
        {/* MER-470: see the tabular branch note. */}
        {data.fallbackPeriodLabel && (
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 6 }}>
            Selected period has no data yet. Showing most recent: {data.fallbackPeriodLabel}.
          </div>
        )}
        <PivotTable
          rowKey={keys[0]}
          colKey={keys[1]}
          measureKey={pivotMeasure ?? keys[2]}
          rows={rows as Record<string, string | number | null>[]}
        />
      </>,
    );
  }

  const supported = SUPPORTED_CHART_TYPES[chartType];
  if (supported) {
    // Manifest-driven chart with declared x_axis + y_axis_series goes
    // through the grouped-aggregate path (MER-142). Each series renders
    // as a Chart.js dataset; chart_subtype determines the visual.
    if (widget.x_axis && widget.y_axis_series && widget.y_axis_series.length > 0) {
      return withNote(
        wrapClickable(
          <ChartWidget
            widget={widget}
            xAxis={widget.x_axis}
            series={widget.y_axis_series}
            chartType={supported}
          />,
        ),
      );
    }
    // MER-462: slice_by + value (the pie shape, e.g. L1b.2 Payment Method
    // Mix) maps onto the same grouped-aggregate path: the slice dimension
    // is the label axis and the single measure is the one dataset. This
    // shape was previously unimplemented, so the widget fell through to
    // the raw-rows path below with no `field` and rendered a bare frame.
    if (widget.slice_by && widget.value) {
      return withNote(
        wrapClickable(
          <ChartWidget
            widget={widget}
            xAxis={widget.slice_by}
            series={[
              {
                column: widget.value.column,
                aggregation: widget.value.aggregation,
                label: widget.name,
              },
            ]}
            chartType={supported}
          />,
        ),
      );
    }
    const rows = data.rows;
    if (rows.length === 0) return withNote(emptyRows());
    const { labels, values } = chartDataFromRows(rows, widget);
    // MER-462: a widget shape the heuristic can't map (no field, or no
    // string label column) yields zero labels. Render the standard empty
    // state, never a Chart.js canvas with bare 0.00-1.00 axes.
    if (labels.length === 0) return withNote(<EmptyState title={effectiveTitle} />);
    return withNote(
      wrapClickable(
        <Chart
          type={supported}
          data={{
            labels,
            datasets: [
              {
                label: widget.name,
                data: values,
                backgroundColor: "rgba(38, 168, 159, 0.75)",
                borderColor: "rgba(15, 107, 92, 1)",
                borderWidth: 1,
              },
            ],
          }}
          format={asKpiFormat(widget.display_format)}
        />,
      ),
    );
  }

  return <ErrorState message={`No renderer for chart_type ${chartType}`} />;
}

export function LockedTab(props: {
  tabLabel: string;
  requiredTier: "growth" | "professional" | "enterprise";
}) {
  return <UpgradeUnlock tabLabel={props.tabLabel} requiredTier={props.requiredTier} />;
}
